import {
    createContext,
    useContext,
    useEffect,
    useState,
    useRef,
    useMemo
} from 'react'
import { useAuth, useUser } from '.'
import { sitesRoutes } from '../routes'
import { sitesDatabase } from '../indexedDB'
import { useConnectionContext } from './Connection.context'

export const SitesContext = createContext()

export const SitesProvider = (props) => {
    const { isAuthenticated, userData } = useAuth()
    const {
        admin,
        isSuperAdmin,
        isOperator,
        isUniversalInspector,
        isUniversalChief,
        isOperatorInspector,
        isSapExecutive,
        isChiefMachinery,
        isShiftManager
    } = useUser()
    const { isOnline } = useConnectionContext()
    const [sites, setSites] = useState([])
    const [sitesToSelection, setSitesToSelection] = useState([])
    const [siteSelected, setSiteSelected] = useState(null)
    const isFetchingRef = useRef(false)

    const isUniversal = isUniversalInspector || isUniversalChief || admin || isSuperAdmin
    const isAdminGlobal = admin || isSuperAdmin || isUniversal
    const isExecutive =
		admin ||
		isSuperAdmin ||
		isUniversal ||
		isSapExecutive ||
		isChiefMachinery ||
		isShiftManager

    const filteredSites = useMemo(() => {
        if (isAdminGlobal || !userData?.obras) {
            return sites
        }
        const userSiteIds = userData.obras.map((o) => {
            if (typeof o === 'object' && o !== null) {
                return String(o._id || o.id)
            }
            return String(o)
        })
        return sites.filter((site) => userSiteIds.includes(String(site._id)))
    }, [sites, userData, isAdminGlobal])

    useEffect(() => {
        if (isAuthenticated) {
            if (isOnline) {
                getSites()
            } else {
                getSitesOffLine()
            }
        }
    }, [isAuthenticated, isOnline, userData])

    useEffect(() => {
        if (filteredSites.length > 0) {
            const allSitesOption = {
                _id: 'all',
                idObra: 'none',
                idObraAlfa: 'ALL',
                descripcion: 'TODAS LAS OBRAS',
                mostrar: true,
                status: true
            }
            const sitesCache = []
            filteredSites.forEach((site) => {
                if (site.mostrar !== false) {
                    sitesCache.push(site)
                }
            })
            if (isUniversal) {
                const universalOption = {
                    _id: 'universal',
                    idObra: 'universal',
                    idObraAlfa: 'UNIVERSAL',
                    descripcion: 'REPORTES UNIVERSALES',
                    mostrar: true,
                    status: true
                }
                setSitesToSelection([allSitesOption, universalOption, ...sitesCache])
            } else {
                setSitesToSelection([allSitesOption, ...sitesCache])
            }
        }
    }, [filteredSites, isUniversal])

    useEffect(() => {
        console.log('Site selected in context:', siteSelected)
    }, [siteSelected])

    useEffect(() => {
        if (userData === undefined) return

        if (isExecutive && sitesToSelection.length > 0 && !siteSelected) {
            const isUniversalUser = isUniversalInspector || isUniversalChief
            if (isUniversalUser) {
                const universalSite = sitesToSelection.find((s) => s._id === 'universal')
                if (universalSite) {
                    console.log('[SitesContext] Pre-selecting universal option for universal role:', universalSite.descripcion)
                    setSiteSelected(universalSite)
                    return
                }
            }
            setSiteSelected(sitesToSelection[0])
        }
    }, [sitesToSelection, siteSelected, isExecutive, isUniversalInspector, isUniversalChief, userData])

    useEffect(() => {
        if (!isAuthenticated) {
            setSiteSelected(null)
        }
    }, [isAuthenticated])

    const getSites = async () => {
        if (isFetchingRef.current) return
        isFetchingRef.current = true
        try {
            const isOperatorOnly = isOperator && !isAdminGlobal && !isExecutive
            if (isOperatorOnly) {
                if (userData?.obras && userData.obras.length > 0) {
                    const { database } = await sitesDatabase.initDbObras()
                    const resolvedSites = []
                    const pendingFetches = []

                    for (const obra of userData.obras) {
                        if (typeof obra === 'object' && obra !== null) {
                            resolvedSites.push(obra)
                            pendingFetches.push(
                                Promise.resolve(obra).then((site) => {
                                    return sitesDatabase.actualizar(
                                        { ...site, id: resolvedSites.indexOf(site) },
                                        database
                                    )
                                })
                            )
                        } else if (typeof obra === 'string') {
                            pendingFetches.push(
                                sitesRoutes
                                    .getSiteById(obra)
                                    .then((response) => {
                                        if (response && response.data) {
                                            const siteData = response.data.data || response.data
                                            resolvedSites.push(siteData)
                                            return sitesDatabase.actualizar(
                                                { ...siteData, id: resolvedSites.length - 1 },
                                                database
                                            )
                                        }
                                    })
                                    .catch((err) => {
                                        console.error(
                                            `Error fetching site details for ${obra}:`,
                                            err
                                        )
                                    })
                            )
                        }
                    }

                    const results = await Promise.allSettled(pendingFetches)
                    const failedCount = results.filter(
                        (r) => r.status === 'rejected'
                    ).length
                    if (failedCount > 0) {
                        console.warn(
                            `[SitesContext] ${failedCount} of ${userData.obras.length} site fetches failed.`
                        )
                    }
                    setSites(resolvedSites.sort(orderByName))
                } else {
                    setSites([])
                }
            } else {
                const response = await sitesRoutes.getSites()
                console.log('response from getSites', response.data)
                const { database } = await sitesDatabase.initDbObras()
                for (const [index, site] of response.data.data.entries()) {
                    site.id = index
                    await sitesDatabase.actualizar(site, database)
                }
                setSites(response.data.data.sort(orderByName))
            }
        } catch (error) {
            console.error('Error in getSites:', error)
        } finally {
            isFetchingRef.current = false
        }
    }

    const getAllSitesIncludingHidden = async () => {
        try {
            const [activeResponse, hiddenResponse] = await Promise.all([
                sitesRoutes.getSites(),
                sitesRoutes.getHiddenSites()
            ])

            const activeSites = activeResponse.data.data || []
            const hiddenSites = hiddenResponse.data.data || []

            const allSites = [...activeSites, ...hiddenSites]
            return allSites.sort(orderByName)
        } catch (error) {
            console.error('Error fetching all sites including hidden', error)
            return []
        }
    }

    const orderByName = (a, b) => {
        if (a.descripcion > b.descripcion) {
            return 1
        }
        if (a.descripcion < b.descripcion) {
            return -1
        }
        return 0
    }

    const getSitesOffLine = async () => {
        const { database } = await sitesDatabase.initDbObras()
        const sitesResponse = await sitesDatabase.consultar(database)
        setSites(sitesResponse)
    }

    const provider = {
        sites: filteredSites,
        siteSelected,
        setSiteSelected,
        sitesToSelection,
        refreshSites: getSites,
        getAllSitesIncludingHidden
    }

    return <SitesContext.Provider value={provider} {...props} />
}

export const useSitesContext = () => useContext(SitesContext)
